/**
 * Types de fiches "autonomes" créables par import batch (une fiche par image).
 * Doit rester aligné avec BATCH_IMPORT_TYPES côté API.
 */
export const BATCH_IMPORT_TYPES = [
  "artwork",
  "multipleprint",
  "document",
  "photography",
  "audiovisual",
  "writing",
] as const;

export type BatchImportType = (typeof BATCH_IMPORT_TYPES)[number];

/** Position "face"/principale de l'image, par type (cf. lib/image.ts mainPositions). */
export const BATCH_IMPORT_MAIN_POSITION: Record<BatchImportType, string> = {
  artwork: "oeuvresPhotoFaceOeuvre",
  multipleprint: "estampesMultiplesDescriptionPhotoFaceOeuvre",
  photography: "photographyPhotoFace",
  audiovisual: "audiovisuelDescriptionPhoto",
  writing: "ecritsPhotoPremierePage",
  document: "documentsPhotoCouverture",
};

/** Base de la route d'édition d'une fiche, par type (route: /edit/<type>/<id>). */
export const BATCH_IMPORT_EDIT_ROUTE: Record<BatchImportType, string> = {
  artwork: "/edit/artwork",
  multipleprint: "/edit/multipleprint",
  document: "/edit/document",
  photography: "/edit/photography",
  audiovisual: "/edit/audiovisual",
  writing: "/edit/writing",
};

/**
 * Formats d'image acceptés à l'import batch (set "étendu sûr").
 * Plus large que le dropzone historique (JPEG/PNG/WebP) mais volontairement
 * restreint aux formats fiables côté convertisseur.
 */
export const BATCH_IMPORT_IMAGE_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/tiff": [".tif", ".tiff"],
  "image/heic": [".heic"],
  "image/avif": [".avif"],
  "image/gif": [".gif"],
};

/**
 * Seuil (Mo) au-delà duquel on prévient l'utilisateur du poids d'une image.
 * Informatif uniquement : le cap dur reste le convertisseur (2 Go) + le quota
 * d'instance.
 */
export const BATCH_IMPORT_WEIGHT_WARN_MB = 25;

export function isBatchImportType(type: string): type is BatchImportType {
  return (BATCH_IMPORT_TYPES as readonly string[]).includes(type);
}

/** Nom de fichier -> titre (sans extension, trim). */
export function fileNameToTitle(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, "").trim() || fileName.trim();
}
